import { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';

import PrivateRoute from './PrivateRoute';
import { Amplify, Auth } from 'aws-amplify';
import awsExports from './aws-exports';

import './css/style.css';
// import './css/labelme.css';  remove to fix css

import './charts/ChartjsConfig';
import { useAuth } from './hooks/useAuth';
import { getUserFranchisesV3, getUserProfileV3 } from './utils/api';
import { Helmet } from 'react-helmet';

// Import pages
import Dashboard from './pages/dashboard/Dashboard';
import Analytics from './pages/dashboard/Analytics';
import Stacks from './pages/dashboard/Stacks';
import Orders from './pages/dashboard/Orders';
import Cameras from './pages/dashboard/Cameras';
import AccountSettings from './pages/dashboard/settings/Account';
import LocationSettings from './pages/dashboard/settings/Location';
import ForgotPasswordRequest from './pages/auth/forgot-password/ForgotPasswordRequest';
import ForgotPasswordSubmit from './pages/auth/forgot-password/ForgotPasswordSubmit';
import Signin from './pages/auth/Signin';
import ChangePasswordPage from './pages/auth/ChangePassword';
import { useData } from './hooks/useData';
import { Roles } from './types/Roles';
import Admin from './pages/dashboard/settings/admin/Admin';
import LabelMe from './pages/dashboard/LabelMe';
import MonitorViewer from './pages/dashboard/MonitorViewer';
import Mothership from './pages/dashboard/Mothership';
import AddressMapPage from './pages/dashboard/AddressMapPage';
import ReportDashboard from './pages/dashboard/ReportDashboard';
import OrdersDashboard from './pages/dashboard/OrdersDashboard';

Amplify.configure(awsExports);
Auth.configure(awsExports);

function App(): JSX.Element {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    profile,
    handleProfileChange,
    setUserFranchises
  } = useData();

  useEffect(() => {
    window.scroll({ top: 0 });
  }, [location.pathname]); // triggered on route change

  // Get the user's profile data.
  useEffect(() => {
    async function bootstrap(): Promise<void> {
      if (auth.user == null) return;

      // --- V3 franchises & profile ---
      // 1) fetch the list
      const franchisesV3 = await getUserFranchisesV3();
      setUserFranchises(franchisesV3);

      // 2) pick the franchiseId: cached or first one
      const cachedFid = localStorage.getItem('selected-franchise-id');
      const franchiseId = cachedFid ?? franchisesV3[0].id;
      if (!franchiseId) {
        console.warn('No franchise available for V3 profile');
        return;
      }

      // 3) fetch the v3 profile for that franchise
      const profileV3 = await getUserProfileV3(franchiseId);
      handleProfileChange(profileV3, franchisesV3);
      if (localStorage.getItem('defaultLocation') === null) {
        localStorage.setItem('defaultLocation', profileV3.locations[0].id);
      }
    }
    bootstrap().catch(console.error);
  }, [auth.user]);

  useEffect(() => {
    // If the profile is loaded, the user is on the homepage, and their role is VIDEO_ONLY...
    if (location.pathname === '/' && profile?.franchise.userRole === Roles.VIDEO_ONLY) {
      // ...redirect them to the cameras page.
      navigate('/cameras', { replace: true });
    }
  }, [profile, location.pathname, navigate]); // This effect runs when the profile or location changes.


  if (auth.isLoading) {
    return <></>;
  }

  // Dynamic Titles for each route
  const getPageTitle = (pathname: string) => {
    switch (pathname) {
      case '/':
        return 'Dashboard - HM';
      case '/analytics':
        return 'Analytics - HM';
      case '/stacks':
        return 'Stacks - HM';
      case '/orders':
        return 'Orders - HM';
      case '/cameras':
        return 'Cameras - HM';
      case '/settings/account':
        return 'Account Settings - HM';
      case '/settings/locations':
        return 'Location Settings - HM';
      case '/labelme':
        return 'Label Me - HM';
      case '/analyticsv2':
        return 'Analytics - HM';
      case '/reports':
        return 'Reports - HM';
      case '/monitorviewer':
        return 'Monitor Viewer - HM';
      case '/mothership':
        return 'Mothership - HM';
      case '/settings/admin':
        return 'Admin Settings - HM';
      default:
        return 'Hellometer';
    }
  };

  return (
    <>
      {/* Set the document title dynamically */}
      <Helmet>
        <title>{getPageTitle(location.pathname)}</title>
      </Helmet>
      <Routes>
        {profile?.franchise.userRole !== Roles.VIDEO_ONLY && <Route
          path="/"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />}
        {profile?.franchise.userRole !== Roles.VIDEO_ONLY && <Route
          path="/analytics"
          element={
            <PrivateRoute>
              <Analytics />
            </PrivateRoute>
          }
        />}
        {/* For testing the new analytics page, not intended for prolonged use */}
        {profile?.franchise.userRole !== Roles.VIDEO_ONLY && <Route
          path="/analyticsv2"
          element={
            <PrivateRoute>
              <Analytics version={'v2'} />
            </PrivateRoute>
          }
        />}
        {profile?.franchise.userRole !== Roles.VIDEO_ONLY && (
          <Route
            path="/stacks"
            element={
              <PrivateRoute>
                <Stacks />
              </PrivateRoute>
            }
          />
        )}
        {profile?.franchise.userRole !== Roles.VIDEO_ONLY && <Route
          path="/orders"
          element={
            <PrivateRoute>
              <OrdersDashboard />
            </PrivateRoute>
          }
        />}
        <Route
          path="/cameras"
          element={
            <PrivateRoute>
              <Cameras />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/account"
          element={
            <PrivateRoute>
              <AccountSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/locations"
          element={
            <PrivateRoute>
              <LocationSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/labelme"
          element={
            <PrivateRoute>
              <LabelMe />
            </PrivateRoute>
          }
        />
        {profile?.franchise.userRole !== Roles.VIDEO_ONLY && <Route
          path="/reports"
          element={
            <PrivateRoute>
              <ReportDashboard />
            </PrivateRoute>
          }
        />}
        <Route
          path="/monitorviewer"
          element={
            <PrivateRoute>
              <MonitorViewer />
            </PrivateRoute>
          }
        />
        <Route
          path="/mothership"
          element={
            <PrivateRoute>
              <Mothership />
            </PrivateRoute>
          }
        />
        <Route
          path="/map"
          element={
            <PrivateRoute>
              <AddressMapPage />
            </PrivateRoute>
          }
        />
        {profile?.franchise.userRole! === Roles.ADMIN && (
          <Route
            path="/settings/admin"
            element={
              <PrivateRoute>
                <Admin />
              </PrivateRoute>
            }
          />
        )}
        <Route path="/forgot-password" element={<ForgotPasswordRequest />} />
        <Route
          path="/forgot-password/submit"
          element={<ForgotPasswordSubmit />}
        />
        <Route path="/signin" element={<Signin />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
      </Routes>
    </>
  );
}

export default App;
